import { HELPS_TEAM_BID, TEAM1, TEAM2 } from './constants';
import { isNilBid } from '../math/spadesMath';
import type {
  AutoGeneratedMade,
  InputValue,
  NilSetting,
  Round,
  TeamKey,
} from '../../types';

export type ScoringTeamNumber = 1 | 2;
export type ScoringPlayer = 'p1' | 'p2';
export type ScoringKind = 'Bid' | 'Made';

export function teamStorageKey(team: ScoringTeamNumber): TeamKey {
  return team === 1 ? TEAM1 : TEAM2;
}

/** `team1BidsAndMade.p1Bid` and the same shape for the other seats. */
export function scoringFieldPath(
  team: ScoringTeamNumber,
  player: ScoringPlayer,
  kind: ScoringKind,
): string {
  return `${teamStorageKey(team)}.${player}${kind}`;
}

/**
 * True only for a player's made field (`p1Made` / `p2Made`).
 * Team keys such as `team1BidsAndMade` also contain "Made", so a substring
 * check on the whole path treats bids as made updates.
 */
export function isPlayerMadeField(fieldToUpdate: string): boolean {
  const playerKey = fieldToUpdate.split('.')[1];
  return playerKey === 'p1Made' || playerKey === 'p2Made';
}

export function autoGeneratedKeyForField(
  fieldToUpdate: string,
): keyof AutoGeneratedMade | null {
  const [teamKey, playerKey] = fieldToUpdate.split('.');
  if (playerKey !== 'p1Made' && playerKey !== 'p2Made') return null;
  const teamNumber = teamKey === TEAM1 ? 1 : teamKey === TEAM2 ? 2 : null;
  if (teamNumber === null) return null;
  return autoKey(teamNumber, playerKey === 'p1Made' ? 'p1' : 'p2');
}

function autoGeneratedFlags(
  round: Round,
  updates: Partial<AutoGeneratedMade> = {},
): AutoGeneratedMade {
  return {
    team1P1: round.autoGeneratedMade?.team1P1 ?? false,
    team1P2: round.autoGeneratedMade?.team1P2 ?? false,
    team2P1: round.autoGeneratedMade?.team2P1 ?? false,
    team2P2: round.autoGeneratedMade?.team2P2 ?? false,
    ...updates,
  };
}

/** Seats whose made value is displayed with an auto-generated asterisk. */
export function teamAutoGeneratedMadePlayers(
  round: Round,
  team: ScoringTeamNumber,
): ScoringPlayer[] {
  return PLAYERS.filter(
    (player) =>
      madeOf(round, team, player) !== '' &&
      round.autoGeneratedMade?.[autoKey(team, player)],
  );
}

/** Undo the team's derived values while preserving manual entries. */
export function undoTeamAutoGeneratedMade(
  round: Round,
  team: ScoringTeamNumber,
): Round {
  const nextTeam = { ...round[teamStorageKey(team)] };
  const flags: Partial<AutoGeneratedMade> = {};
  for (const player of teamAutoGeneratedMadePlayers(round, team)) {
    nextTeam[`${player}Made`] = '';
    flags[autoKey(team, player)] = false;
  }
  return {
    ...round,
    [teamStorageKey(team)]: nextTeam,
    autoGeneratedMade: autoGeneratedFlags(round, flags),
  };
}

/** Preserve one manual seat if possible; otherwise split, with the odd trick to P1. */
export function applyTeamMadeTotal(
  currentRound: Round,
  team: ScoringTeamNumber,
  total: number,
): Round {
  const manual = PLAYERS.filter((player) =>
    isHandEntered(currentRound, team, player),
  );
  if (manual.length === 1) {
    const known = manual[0];
    const count = Number(madeOf(currentRound, team, known));
    if (count <= total) {
      const other = known === 'p1' ? 'p2' : 'p1';
      return withMade(currentRound, team, {
        [other]: { made: total - count, auto: true },
      });
    }
  }
  return withMade(currentRound, team, {
    p1: { made: Math.ceil(total / 2), auto: true },
    p2: { made: Math.floor(total / 2), auto: true },
  });
}

/** A hand-entered made value drops that seat's asterisk. Bids are left alone. */
export function clearAutoGeneratedMade(
  round: Round,
  fieldToUpdate: string,
): Round {
  const key = autoGeneratedKeyForField(fieldToUpdate);
  if (!key) return round;
  return {
    ...round,
    autoGeneratedMade: autoGeneratedFlags(round, { [key]: false }),
  };
}

// ─── Team made entry (team-made-modal.md, chat 15) ─────────────────────────

const PLAYERS: ScoringPlayer[] = ['p1', 'p2'];

export { isNilBid };

/** The tricks a bid aims for: a nil aims for 0. Empty for no bid yet. */
export function bidTarget(bid: InputValue | undefined): number | undefined {
  if (isNilBid(bid)) return 0;
  const text = String(bid ?? '');
  return /^\d+$/.test(text) ? Number(text) : undefined;
}

function autoKey(
  team: ScoringTeamNumber,
  player: ScoringPlayer,
): keyof AutoGeneratedMade {
  return `team${team}${player === 'p1' ? 'P1' : 'P2'}` as keyof AutoGeneratedMade;
}

function madeOf(round: Round, team: ScoringTeamNumber, player: ScoringPlayer) {
  return String(round[teamStorageKey(team)]?.[`${player}Made`] ?? '');
}

function bidOf(round: Round, team: ScoringTeamNumber, player: ScoringPlayer) {
  return round[teamStorageKey(team)]?.[`${player}Bid`];
}

/** A made value that was picked for that seat, not derived from a total. */
function isHandEntered(
  round: Round,
  team: ScoringTeamNumber,
  player: ScoringPlayer,
): boolean {
  return (
    madeOf(round, team, player) !== '' &&
    !round.autoGeneratedMade?.[autoKey(team, player)]
  );
}

function withMade(
  round: Round,
  team: ScoringTeamNumber,
  values: Partial<Record<ScoringPlayer, { made: number; auto: boolean }>>,
): Round {
  const teamField = teamStorageKey(team);
  const nextTeam = { ...round[teamField] };
  const flags: Partial<AutoGeneratedMade> = {};
  for (const player of PLAYERS) {
    const value = values[player];
    if (!value) continue;
    nextTeam[`${player}Made`] = String(value.made) as InputValue;
    flags[autoKey(team, player)] = value.auto;
  }
  return {
    ...round,
    [teamField]: nextTeam,
    autoGeneratedMade: autoGeneratedFlags(round, flags),
  };
}

export type TeamMadeFlow = 'total' | 'nil_check' | 'per_player';

export function teamMadeFlow(
  round: Round,
  team: ScoringTeamNumber,
  nilSetting: NilSetting,
): TeamMadeFlow {
  const nilSeats = PLAYERS.filter((p) => isNilBid(bidOf(round, team, p)));
  if (nilSeats.length === 0) return 'total';
  // A double nil has no partner's bid to help: every trick is a bag, so a team
  // total adds nothing to each player's count.
  return nilSetting === HELPS_TEAM_BID && nilSeats.length === 1
    ? 'nil_check'
    : 'per_player';
}

export interface TeamMadePlan {
  flow: Exclude<TeamMadeFlow, 'total'>;
  /** nil_check: the one nil seat, asked "0 tricks" or "1 or more". */
  questions: ScoringPlayer[];
  /** per_player: seats with their own made grid, nil seats first. */
  grids: ScoringPlayer[];
}

export interface TeamMadeAnswers {
  nilMade?: Partial<Record<ScoringPlayer, boolean>>;
  total?: number;
  made?: Partial<Record<ScoringPlayer, number>>;
}

/** The team's seats with nil bidders first: the order the sheet lists them. */
export function nilSeatsFirst(
  round: Round,
  team: ScoringTeamNumber,
): ScoringPlayer[] {
  const isNil = (p: ScoringPlayer) => isNilBid(bidOf(round, team, p));
  return [...PLAYERS.filter(isNil), ...PLAYERS.filter((p) => !isNil(p))];
}

export function planTeamMade(
  round: Round,
  team: ScoringTeamNumber,
  nilSetting: NilSetting,
): TeamMadePlan {
  // Every seat is in the sheet, nil bidders first; the round's values start
  // selected (initialTeamMadeAnswers), so past rounds edit like the current.
  const seats = nilSeatsFirst(round, team);
  if (teamMadeFlow(round, team, nilSetting) === 'nil_check') {
    return {
      flow: 'nil_check',
      questions: seats.filter((p) => isNilBid(bidOf(round, team, p))),
      grids: [],
    };
  }
  return { flow: 'per_player', questions: [], grids: seats };
}

/**
 * A broken nil keeps the count it was given by hand. An empty seat, or one
 * filled in from a team total, has an unknown count (at least 1).
 */
function brokenNilCount(
  round: Round,
  team: ScoringTeamNumber,
  player: ScoringPlayer,
): number | null {
  if (!isHandEntered(round, team, player)) return null;
  const count = parseInt(madeOf(round, team, player), 10);
  return count >= 1 ? count : null;
}

/**
 * The sheet starts on the round's values: each grid's pick, each nil
 * answer, and the team total once both seats are in.
 */
export function initialTeamMadeAnswers(
  round: Round,
  team: ScoringTeamNumber,
  plan: TeamMadePlan,
): TeamMadeAnswers {
  const valueOf = (player: ScoringPlayer) => {
    const made = madeOf(round, team, player);
    return made === '' ? undefined : parseInt(made, 10);
  };
  if (plan.flow === 'per_player') {
    const made: TeamMadeAnswers['made'] = {};
    for (const player of plan.grids) {
      const value = valueOf(player);
      if (value !== undefined) made[player] = value;
    }
    return { made };
  }
  const nilMade: TeamMadeAnswers['nilMade'] = {};
  for (const player of plan.questions) {
    const value = valueOf(player);
    if (value !== undefined) nilMade[player] = value === 0;
  }
  const [p1, p2] = PLAYERS.map(valueOf);
  return p1 === undefined || p2 === undefined
    ? { nilMade }
    : { nilMade, total: p1 + p2 };
}

/**
 * nil_check: the totals the grid accepts once the nil is answered (null before
 * that). A broken nil counts its hand-entered tricks, or 1 if they're unknown.
 */
export function teamMadeTotalRange(
  round: Round,
  team: ScoringTeamNumber,
  plan: TeamMadePlan,
  answers: TeamMadeAnswers,
): { min: number; max: number } | null {
  if (plan.questions.some((p) => answers.nilMade?.[p] === undefined)) {
    return null;
  }
  let min = 0;
  for (const player of plan.questions) {
    if (answers.nilMade?.[player] === false) {
      min += brokenNilCount(round, team, player) ?? 1;
    }
  }
  return { min, max: 13 };
}

/**
 * nil_check: the lowest team total the grid accepts right now. An unanswered
 * nil counts as held, so the grid can be picked before the answers.
 */
export function teamMadeTotalFloor(
  round: Round,
  team: ScoringTeamNumber,
  plan: TeamMadePlan,
  answers: TeamMadeAnswers,
): number {
  const nilMade = Object.fromEntries(
    plan.questions.map((p) => [p, answers.nilMade?.[p] ?? true]),
  );
  return (
    teamMadeTotalRange(round, team, plan, { ...answers, nilMade })?.min ?? 0
  );
}

/** per_player: the highest pick a seat can take without the team passing 13. */
export function teamMadePlayerMax(
  answers: TeamMadeAnswers,
  player: ScoringPlayer,
): number {
  const other = PLAYERS.find((p) => p !== player)!;
  return 13 - (answers.made?.[other] ?? 0);
}

export function isTeamMadeComplete(
  round: Round,
  team: ScoringTeamNumber,
  plan: TeamMadePlan,
  answers: TeamMadeAnswers,
): boolean {
  if (plan.flow === 'per_player') {
    const picks = plan.grids.map((p) => answers.made?.[p]);
    if (picks.some((v) => v === undefined)) return false;
    return picks.reduce<number>((sum, v) => sum + (v ?? 0), 0) <= 13;
  }
  const range = teamMadeTotalRange(round, team, plan, answers);
  if (!range) return false;
  const { total } = answers;
  return total !== undefined && total >= range.min && total <= range.max;
}

/** Writes a complete set of answers ({@link isTeamMadeComplete}) to the round. */
export function applyTeamMadeAnswers(
  round: Round,
  team: ScoringTeamNumber,
  plan: TeamMadePlan,
  answers: TeamMadeAnswers,
): Round {
  if (plan.flow === 'per_player') {
    const values: Partial<
      Record<ScoringPlayer, { made: number; auto: boolean }>
    > = {};
    for (const player of plan.grids) {
      values[player] = { made: answers.made?.[player] ?? 0, auto: false };
    }
    return withMade(round, team, values);
  }

  // The split doesn't change the helpsTeamBid score, so a broken nil with no
  // count of its own is stored as 1 trick (decided in chat 15).
  const [nil, partner] = nilSeatsFirst(round, team);
  const count = answers.nilMade?.[nil] ? 0 : brokenNilCount(round, team, nil);
  const nilMade = count ?? 1;
  const values: Partial<
    Record<ScoringPlayer, { made: number; auto: boolean }>
  > = {};
  values[nil] = { made: nilMade, auto: count === null };
  values[partner] = { made: (answers.total ?? 0) - nilMade, auto: true };
  return withMade(round, team, values);
}
